import React, { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import FluidImage from "@/components/images/fluid-image";
import NativeSelect from "@/components/ui/input/native-select";
import Checkbox from "@/components/ui/input/checkbox";
import { CustomerPaymentMethod } from "@/networking/payments.api";
import { useTranslation } from "@/hooks/useTranslation";
import useCartCountry from "@/components/checkout/hooks/useCartCountry";
import useAutocomplete from "@/components/checkout/hooks/useAutocomplete";
import { CountriesAndLanguages } from "@/networking/addresses.api";
import { useDynamicAddressResolver } from "@/hooks/useAddressFields";
import DynamicAddressFields from "@/components/shared/dynamic-address-fields";
import {
  resolvePaymentMethodDisplayLabel,
  resolvePaymentMethodLogoUrl,
} from "@/lib/payment-method-display";
import {
  createEditPaymentMethodFormSchema,
  type EditPaymentMethodFormData,
  type InternalFormData,
} from "@/components/management/edit-payment-method-dialog.types";

export type {
  EditPaymentMethodFormData,
  InternalFormData,
} from "@/components/management/edit-payment-method-dialog.types";

interface EditPaymentMethodDialogProps {
  isOpen: boolean;
  paymentMethod: CustomerPaymentMethod | null;
  onClose: () => void;
  onSubmit: (data: EditPaymentMethodFormData) => void;
  isSubmitting: boolean;
  error?: string;
  countries?: CountriesAndLanguages["countries"];
  cartToken?: string;
}

export default function EditPaymentMethodDialog({
  isOpen,
  paymentMethod,
  onClose,
  onSubmit,
  isSubmitting,
  error,
  countries = [],
  cartToken,
}: EditPaymentMethodDialogProps) {
  const { t } = useTranslation();

  const initialCountryCode =
    paymentMethod?.billing_address?.country_code || "US";

  const { stateOptions, handleCountryChange, addressFields } = useCartCountry(
    cartToken || "",
    null,
    initialCountryCode,
    countries,
  );

  const resolver = useDynamicAddressResolver<InternalFormData>(
    addressFields,
    (fields) => createEditPaymentMethodFormSchema(fields),
  );

  const { control, handleSubmit, reset, setValue } = useForm<InternalFormData>({
    resolver,
    defaultValues: {
      billing_address: {
        name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postal_code: "",
        country_code: "US",
        country: "",
      },
      set_as_default: false,
    },
  });

  const billingCountryCode = useWatch({
    control,
    name: "billing_address.country_code",
  });
  const setAsDefault = useWatch({ control, name: "set_as_default" });
  const billingCountryName = useWatch({
    control,
    name: "billing_address.country",
  });

  const {
    getAutocompleteOptions,
    handleOptionClick,
    renderAutocompleteOptions,
  } = useAutocomplete<InternalFormData>(
    setValue,
    "billing_address.city",
    "billing_address.state",
    "billing_address.postal_code",
    "billing_address.address1",
    stateOptions,
  );

  useEffect(() => {
    if (paymentMethod && isOpen) {
      const billingAddress = paymentMethod.billing_address;

      const countryCode = billingAddress?.country_code || "US";
      const countryName =
        countries.find((c) => c.iso === countryCode)?.name || "United States";

      reset({
        billing_address: {
          name: billingAddress?.name || "",
          address1: billingAddress?.address1 || "",
          address2: billingAddress?.address2 || "",
          city: billingAddress?.city || "",
          state: billingAddress?.state || "",
          postal_code: billingAddress?.zip || "",
          country_code: countryCode,
          country: countryName,
        },
        set_as_default: paymentMethod.default,
      });
    }
  }, [paymentMethod, isOpen, reset, countries]);

  const handleCountrySelect = async (countryCode: string) => {
    await handleCountryChange(countryCode, false);
    setValue("billing_address.country_code", countryCode, {
      shouldValidate: true,
    });
    const countryName =
      countries.find((c) => c.iso === countryCode)?.name || "";
    setValue("billing_address.country", countryName);
    setValue("billing_address.address1", "");
    setValue("billing_address.address2", "");
    setValue("billing_address.city", "");
    setValue("billing_address.state", "");
    setValue("billing_address.postal_code", "");
  };

  const handleFormSubmit = handleSubmit((data) => {
    const billingAddress = data.billing_address;

    onSubmit({
      billing_address: {
        name: billingAddress.name || "",
        address1: billingAddress.address1 || "",
        address2: billingAddress.address2 || "",
        city: billingAddress.city || "",
        state: billingAddress.state || "",
        zip: billingAddress.postal_code || "",
        country_code: billingAddress.country_code,
        phone: billingAddress.phone || undefined,
      },
      set_as_default: data.set_as_default,
    });
  });

  if (!paymentMethod) return null;

  const { details } = paymentMethod;
  const cardLabel = resolvePaymentMethodDisplayLabel({
    lastFour: details.last_four,
    cardBrand: details.card_brand,
    cardType: details.card_type,
    detailsType: details.type,
    paymentTitle: paymentMethod.payment_title,
    paymentType: paymentMethod.payment_type,
    source: paymentMethod.source,
  });
  const logoUrl = resolvePaymentMethodLogoUrl({
    logoUrl: details.logo_url,
    cardBrand: details.card_brand,
    cardType: details.card_type,
    detailsType: details.type,
    paymentTitle: paymentMethod.payment_title,
    paymentType: paymentMethod.payment_type,
    source: paymentMethod.source,
  });
  const logoAlt =
    details.card_brand || details.card_type || t("payment_method");
  const expMonth = details.exp_month;
  const expYear = details.exp_year;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-sm overflow-y-auto rounded md:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-md font-medium">
            {t("edit_card")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="flex items-center space-x-3">
              {logoUrl && (
                <div className="h-6 w-10 shrink-0">
                  <FluidImage
                    src={logoUrl}
                    alt={logoAlt}
                    width={40}
                    height={24}
                    className="object-contain"
                  />
                </div>
              )}
              <div className="flex flex-col">
                {cardLabel && (
                  <span className="text-sm font-medium text-gray-900">
                    {cardLabel}
                  </span>
                )}
                {expMonth && expYear && (
                  <span className="text-sm text-gray-500">
                    {t("card_expires")} {expMonth}/{expYear}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-1 text-sm font-medium text-gray-900">
              {t("billing_address")}
            </h3>
            <div className="rounded shadow-xs">
              <NativeSelect
                key={billingCountryCode || "no-country"}
                control={control}
                label={t("country")}
                placeholder={t("select_an_option")}
                options={
                  countries
                    ?.map((country) => ({
                      id: country.iso,
                      name: country.name,
                    }))
                    .sort((a, b) => {
                      if (a.id === billingCountryCode) return -1;
                      if (b.id === billingCountryCode) return 1;
                      return a.name.localeCompare(b.name);
                    }) || []
                }
                name="billing_address.country_code"
                onSelectChange={(e) => {
                  handleCountrySelect(e.id.toString());
                }}
                inputStyles="rounded-t"
              />
              <DynamicAddressFields
                addressFields={addressFields}
                stateOptions={stateOptions}
                fieldPathPrefix="billing_address"
                control={control}
                formCountry={billingCountryName || ""}
                countryCode={billingCountryCode}
                renderAutocompleteOptions={renderAutocompleteOptions}
                getAutocompleteOptions={getAutocompleteOptions}
                handleOptionClick={handleOptionClick}
              />
            </div>
          </div>
        </div>

        <div className="mt-2">
          <Checkbox
            control={control}
            name="set_as_default"
            id="set_as_default"
            label={t("set_as_default_payment_method")}
            classNames="text-sm"
            onChange={() => {
              setValue("set_as_default", !setAsDefault);
            }}
          />
        </div>

        {error && <div className="text-sm text-red-500">{error}</div>}

        <DialogFooter className="flex flex-row items-center justify-between">
          <div className="flex-2">
            <Button
              type="button"
              className="rounded bg-gray-50 p-3 text-gray-900 ring-1 ring-gray-300 hover:bg-gray-100"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </Button>
          </div>
          <div className="flex-1 text-right">
            <Button
              type="submit"
              onClick={handleFormSubmit}
              disabled={isSubmitting}
              className="rounded bg-gray-900 p-3 text-white hover:bg-gray-800"
            >
              {isSubmitting ? (
                <div className="h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent"></div>
              ) : (
                t("save")
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
